import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CommonDialog from "app/components/common/CommonDialog";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import {
  CustomTextField,
  CustomTextArea,
  CustomSelectBox,
} from "assets/styles/globalStyledComponent";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { createPlan, updatePlan } from "app/services/org/subscription.service";

const initialValues = {
  planName: "",
  description: "",
  billingCycle: "monthly", // monthly, quarterly, annual
  price: "",
  productId: "",
  status: "active",
  isPublic: false,
  features: [],
  maxUsers: "",
  maxProjects: "",
};

const AddEditPlanDialog = ({
  open = false,
  onClose = () => {},
  refetch = () => {},
  edit = false,
  editData = {},
  productList = [],
}) => {
  const [state, setState] = useState({
    featureInput: "",
    features: [],
  });

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: initialValues,
  });

  // Watch features from form
  const watchedFeatures = watch("features");

  useEffect(() => {
    if (open) {
      if (edit && editData?.id) {
        reset({
          planName: editData?.planName || "",
          description: editData?.description || "",
          billingCycle: editData?.billingCycle || "monthly",
          price: editData?.price || "",
          productId: editData?.productId || "",
          status: editData?.status || "active",
          isPublic: editData?.isPublic || false,
          features: editData?.features || [],
          maxUsers: editData?.maxUsers || "",
          maxProjects: editData?.maxProjects || "",
        });
        setState((prev) => ({
          ...prev,
          features: editData?.features || [],
        }));
      } else {
        reset(initialValues);
        setState((prev) => ({
          ...prev,
          featureInput: "",
          features: [],
        }));
      }
    }
  }, [open, edit, editData, reset]);

  const { mutate: createPlanMutate, isLoading: createPlanLoading } =
    useMutation("createPlan", createPlan, {
      onSuccess: (success) => {
        toast.success(success?.data?.message || "Plan created successfully");
        refetch();
        handleClose();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to create plan");
      },
    });

  const { mutate: updatePlanMutate, isLoading: updatePlanLoading } =
    useMutation("updatePlan", ({ planId, payload }) => updatePlan(planId, payload), {
      onSuccess: (success) => {
        toast.success(success?.data?.message || "Plan updated successfully");
        refetch();
        handleClose();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to update plan");
      },
    });

  const handleAddFeature = () => {
    if (state.featureInput.trim()) {
      setState((prev) => ({
        ...prev,
        features: [...prev.features, state.featureInput.trim()],
        featureInput: "",
      }));
    }
  };

  const handleRemoveFeature = (index) => {
    setState((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleClose = () => {
    reset(initialValues);
    setState({ featureInput: "", features: [] });
    onClose();
  };

  const onSubmit = (formData) => {
    const payload = {
      ...formData,
      features: state.features,
      price: parseFloat(formData.price),
      maxUsers: formData.maxUsers ? parseInt(formData.maxUsers) : null,
      maxProjects: formData.maxProjects ? parseInt(formData.maxProjects) : null,
    };

    if (edit && editData?.id) {
      updatePlanMutate({ planId: editData.id, payload });
    } else {
      createPlanMutate(payload);
    }
  };

  const isLoading = createPlanLoading || updatePlanLoading;

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title={edit ? "Edit Plan" : "Add New Plan"}
      maxWidth="sm"
    >
      <DialogContent sx={{ minWidth: "500px" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Plan Name */}
            <Grid item xs={12}>
              <TextFieldLabel>Plan Name</TextFieldLabel>
              <Controller
                name="planName"
                control={control}
                rules={{ required: "Plan name is required" }}
                render={({ field, fieldState: { error } }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    placeholder="e.g., Professional, Enterprise"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextFieldLabel>Description</TextFieldLabel>
              <Controller
                name="description"
                control={control}
                rules={{ required: "Description is required" }}
                render={({ field, fieldState: { error } }) => (
                  <CustomTextArea
                    {...field}
                    placeholder="Plan description"
                    style={{
                      width: "100%",
                      height: "80px",
                      padding: "10px",
                      fontSize: "14px",
                      border: error ? "1px solid crimson" : "1.5px solid #B6C8D6",
                      borderRadius: "8px",
                      fontFamily: "Inter, sans-serif",
                    }}
                  />
                )}
              />
              {/* Dynamically render error inside or outside TextArea */}
            </Grid>

            {/* Product */}
            <Grid item xs={12} md={6}>
              <TextFieldLabel>Product</TextFieldLabel>
              <Controller
                name="productId"
                control={control}
                rules={{ required: "Product is required" }}
                render={({ field, fieldState: { error } }) => (
                  <CustomSelectBox
                    {...field}
                    size="small"
                    fullWidth
                    displayEmpty
                    error={!!error}
                  >
                    <MenuItem value="" disabled>
                      Select Product
                    </MenuItem>
                    {productList?.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.productName || product.name}
                      </MenuItem>
                    ))}
                  </CustomSelectBox>
                )}
              />
            </Grid>

            {/* Billing Cycle */}
            <Grid item xs={12} md={6}>
              <TextFieldLabel>Billing Cycle</TextFieldLabel>
              <Controller
                name="billingCycle"
                control={control}
                rules={{ required: "Billing cycle is required" }}
                render={({ field, fieldState: { error } }) => (
                  <CustomSelectBox
                    {...field}
                    size="small"
                    fullWidth
                    error={!!error}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </CustomSelectBox>
                )}
              />
            </Grid>

            {/* Price */}
            <Grid item xs={12} md={6}>
              <TextFieldLabel>Price</TextFieldLabel>
              <Controller
                name="price"
                control={control}
                rules={{
                  required: "Price is required",
                  pattern: { value: /^[0-9]+(\.[0-9]{1,2})?$/, message: "Enter valid price" },
                }}
                render={({ field, fieldState: { error } }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="0.00"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Max Users */}
            <Grid item xs={12} md={6}>
              <TextFieldLabel>Max Users (Optional)</TextFieldLabel>
              <Controller
                name="maxUsers"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="Unlimited"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Max Projects */}
            <Grid item xs={12} md={6}>
              <TextFieldLabel>Max Projects (Optional)</TextFieldLabel>
              <Controller
                name="maxProjects"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <CustomTextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="Unlimited"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <TextFieldLabel>Status</TextFieldLabel>
              <Controller
                name="status"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <CustomSelectBox
                    {...field}
                    size="small"
                    fullWidth
                    error={!!error}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                  </CustomSelectBox>
                )}
              />
            </Grid>

            {/* Public */}
            <Grid item xs={12} md={6}>
              <Controller
                name="isPublic"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Public Plan"
                  />
                )}
              />
            </Grid>

            {/* Features */}
            <Grid item xs={12}>
              <TextFieldLabel>Features</TextFieldLabel>
              <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Add feature (e.g., API Access)"
                  value={state.featureInput}
                  onChange={(e) =>
                    setState((prev) => ({
                      ...prev,
                      featureInput: e.target.value,
                    }))
                  }
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddFeature();
                    }
                  }}
                />
                <LoadingButton
                  type="button"
                  variant="contained"
                  onClick={handleAddFeature}
                  sx={{ width: "100px" }}
                >
                  Add
                </LoadingButton>
              </div>
              {state.features.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {state.features.map((feature, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px",
                        backgroundColor: "#f6f8fc",
                        borderRadius: "4px",
                        marginBottom: "4px",
                      }}
                    >
                      <span>{feature}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFeature(index)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "red",
                          cursor: "pointer",
                          fontSize: "18px",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <LoadingButton
          onClick={handleClose}
          variant="outlined"
          disabled={isLoading}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          variant="contained"
        >
          {edit ? "Update" : "Create"}
        </LoadingButton>
      </DialogActions>
    </CommonDialog>
  );
};

export default AddEditPlanDialog;
